import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaInfoCircle } from "react-icons/fa";
import {
  getOperatorsForField,
  getFieldDataType,
  isNumericDataType,
  isAutonumberDataType,
  isOperatorValueRequired,
  isBetweenOperator,
  isPicklistField,
  isBooleanField,
  isMultipleValuesAllowed,
  getFieldByName,
  normalizeFilterValue,
  parseBetweenFilterValue,
  encodeBetweenFilterValue,
} from "../../utils/conditionBuilderUtils";
import {
  FILTER_VALUE_TYPE_STATIC,
  FILTER_VALUE_TYPE_FIELD,
  FILTER_VALUE_TYPE_LOGGED_USER,
  FILTER_VALUE_TYPE_LITERAL,
  getFilterValueType,
  filterValueFieldsForLeftField,
  encodeFieldCompareValue,
  decodeFieldCompareValue,
  encodeLoggedUserCompareValue,
  decodeLoggedUserCompareValue,
  LOGGED_USER_COMPARE_FIELDS,
  resolveFieldApiNameForCompare,
  resolveReportFieldComparePickerValue,
  isFieldMergeToken,
  isLoggedUserMergeToken,
} from "../../utils/filterFieldValueUtils";
import {
  isDateOrDateTimeFieldType,
  validateDateFilterBetweenLiteralValue,
  validateDateFilterLiteralValue,
} from "../../utils/dateLiteralUtils";
import {
  standardInputStyles,
  standardLabelStyles,
  standardInputContainerHeight,
} from "../../utils/conditionInputStyles";
import PicklistFieldSelector from "../workflow/PicklistFieldSelector";
import FieldSelectorDropdown from "./FieldSelectorDropdown";
import LoggedUserMergeTextInput from "./LoggedUserMergeTextInput";
import LookupLayout from "../layout/LookupLayout";
import { scopeRecordviewToObjectNameForField } from "../../utils/recordviewLookup";
import { extractLookupFilter } from "../../utils/lookupFilterUtils";

/**
 * Reusable Condition Row Component
 * Displays Field-Operator-Value inputs with proper filtering and value types
 *
 * @param {Object} props
 * @param {Object} condition - Condition object with fieldName/field, operator, value
 * @param {Array} fields - Array of available fields
 * @param {Function} onChange - Callback when condition changes (index, field, value)
 * @param {number} index - Index of the condition
 * @param {boolean} readOnly - Whether inputs are disabled
 * @param {Object} options - Additional options:
 *   - fieldKey: Key name for field in condition object (default: 'fieldName' or 'field')
 *   - valueKey: Key name for value in condition object (default: 'value')
 *   - operatorKey: Key name for operator in condition object (default: 'operator')
 *   - nameKey: Key name for field name in fields array (default: 'field_name')
 *   - labelKey: Key name for field label in fields array (default: 'field_label')
 *   - customOperators: Custom operators array
 *   - picklistOperators: Custom picklist operators array
 *   - showFieldLabel: Show field label dropdown (default: true)
 *   - fieldColSize: Column size for field (default: 3)
 *   - operatorColSize: Column size for operator (default: 2)
 *   - valueColSize: Column size for value (default: 3)
 */
export default function ConditionRow({
  condition,
  fields = [],
  onChange,
  index,
  readOnly = false,
  options = {},
  /** Optional map fieldId -> [{ key, value }] for prefetched picklist options (list view filters). */
  picklistPrefetchByFieldId = null,
}) {
  // Extract options with defaults
  const {
    fieldKey = condition.fieldName !== undefined ? 'fieldName' : 'field',
    valueKey = 'value',
    operatorKey = 'operator',
    nameKey = 'field_name',
    labelKey = 'field_label',
    customOperators,
    picklistOperators,
    showFieldLabel = true,
    fieldColSize = 3,
    operatorColSize = 2,
    valueColSize = 3,
    useSearchableDropdown = true, // New option to use searchable dropdown
    fieldTypeFilter = null, // Optional field type filter
    fieldFilter = null, // Optional custom field filter function
    hideSubtitle = false,
    subtitleKey = null,
    /** When true, field is shown but cannot be changed (e.g. report run-time filters). */
    fieldReadOnly = false,
    /** When true, omit the field column (operator + value only). */
    hideFieldColumn = false,
    /** Allow comparing to another field (static vs field). Default true. */
    allowFieldValue = true,
    /** Allow comparing to logged-in user fields ({{LoggedUser.id}}, etc.). Default false. */
    allowLoggedUserValue = false,
    /** Fields available when valueType is field (defaults to `fields`). */
    valueFields = null,
    valueTypeKey = 'valueType',
    /** Optional: apply multiple keys in one state update (avoids losing values when toggling static/field). */
    onPatch = null,
    /**
     * How field-compare values are stored: 'field_name' (workflow/validation) or
     * 'id' (report field_config_id in {{uuid}}).
     */
    fieldCompareStoreKey = null,
    /**
     * Current CRM object API name (e.g. contact). Used to scope recordview
     * lookups so list/edit filters only show recordviews for that object.
     */
    layoutObjectName = null,
  } = options;

  const compareStoreAsFieldConfigId = fieldCompareStoreKey === 'id';

  const patchCondition = (patch) => {
    if (typeof onPatch === 'function') {
      onPatch(index, patch);
      return;
    }
    Object.entries(patch).forEach(([k, v]) => onChange(index, k, v));
  };

  const valueFieldOptions = valueFields != null ? valueFields : fields;

  // Get current field name and field object
  const fieldName = condition[fieldKey] || '';
  const selectedField = getFieldByName(fields, fieldName, { nameKey });

  // Get operators for the selected field
  const operators = getOperatorsForField(selectedField, {
    customOperators,
    picklistOperators,
  });

  const currentOperator = condition[operatorKey] || '';
  const isBetween = isBetweenOperator(currentOperator);

  // Check if value is required for the operator
  const valueRequired = isOperatorValueRequired(currentOperator);

  // Check if multiple values are allowed
  const allowMultiple = isMultipleValuesAllowed(selectedField, condition[operatorKey] || '');

  // Check if field is a lookup/reference field
  const isLookupField = selectedField && (
    selectedField.data_type === 'lookup' ||
    selectedField.data_type === 'reference' ||
    selectedField.is_reference === true
  ) && (selectedField.reference_object || selectedField.referenceObject);

  // Extract lookup filter similar to EditLayout (shared util)

  const valueType = getFilterValueType(condition);
  const fieldDataType = getFieldDataType(selectedField);
  const isDateField = isDateOrDateTimeFieldType(fieldDataType);
  const showLoggedUserValueOption = allowLoggedUserValue && allowFieldValue;
  const showValueTypeToggle =
    (allowFieldValue || showLoggedUserValueOption) &&
    valueRequired &&
    !readOnly &&
    (!isBetween || isDateField);
  const showLiteralValueOption = showValueTypeToggle && isDateField;

  const [dateLiteralInvalid, setDateLiteralInvalid] = useState(false);

  /** Keep static / field / literal drafts while toggling type (parent onChange is one key per call). */
  const valueDraftRef = useRef({
    staticValue: '',
    fieldValue: '',
    literalValue: '',
    staticDisplayValue: '',
  });

  const syncDraftFromCondition = () => {
    const v = condition[valueKey] ?? '';
    const d = condition.displayValue ?? '';
    const vt = getFilterValueType(condition);
    if (vt === FILTER_VALUE_TYPE_FIELD) {
      valueDraftRef.current.fieldValue = v;
    } else if (vt === FILTER_VALUE_TYPE_LOGGED_USER) {
      valueDraftRef.current.loggedUserValue = v;
    } else if (vt === FILTER_VALUE_TYPE_LITERAL) {
      valueDraftRef.current.literalValue = v;
    } else {
      valueDraftRef.current.staticValue = v;
      valueDraftRef.current.staticDisplayValue = d;
    }
  };

  const comparableValueFields = filterValueFieldsForLeftField(
    valueFieldOptions,
    fieldName,
    nameKey
  );

  // Handle field change
  const handleFieldChange = (newFieldName) => {
    const trimmedField =
      typeof newFieldName === 'string' ? newFieldName.trim() : newFieldName;
    onChange(index, fieldKey, trimmedField);

    // Reset operator and value when field changes
    if (operators.length > 0) {
      onChange(index, operatorKey, operators[0].value);
    } else {
      onChange(index, operatorKey, '');
    }
    onChange(index, valueKey, '');
    onChange(index, 'displayValue', '');
    onChange(index, valueTypeKey, FILTER_VALUE_TYPE_STATIC);
  };

  const handleValueTypeChange = (nextType) => {
    if (valueType === FILTER_VALUE_TYPE_FIELD) {
      valueDraftRef.current.fieldValue = condition[valueKey] ?? '';
    } else if (valueType === FILTER_VALUE_TYPE_LOGGED_USER) {
      valueDraftRef.current.loggedUserValue = condition[valueKey] ?? '';
    } else if (valueType === FILTER_VALUE_TYPE_LITERAL) {
      valueDraftRef.current.literalValue = condition[valueKey] ?? '';
    } else {
      valueDraftRef.current.staticValue = condition[valueKey] ?? '';
      valueDraftRef.current.staticDisplayValue = condition.displayValue ?? '';
    }

    setDateLiteralInvalid(false);

    if (nextType === FILTER_VALUE_TYPE_FIELD) {
      patchCondition({
        [valueTypeKey]: nextType,
        [valueKey]: valueDraftRef.current.fieldValue,
        displayValue: '',
      });
    } else if (nextType === FILTER_VALUE_TYPE_LOGGED_USER) {
      patchCondition({
        [valueTypeKey]: nextType,
        [valueKey]: valueDraftRef.current.loggedUserValue,
        displayValue: '',
      });
    } else if (nextType === FILTER_VALUE_TYPE_LITERAL) {
      patchCondition({
        [valueTypeKey]: nextType,
        [valueKey]: valueDraftRef.current.literalValue,
        displayValue: '',
      });
    } else {
      patchCondition({
        [valueTypeKey]: nextType,
        [valueKey]: valueDraftRef.current.staticValue,
        displayValue: valueDraftRef.current.staticDisplayValue,
      });
    }
  };

  // Handle operator change
  const handleOperatorChange = (newOperator) => {
    const trimmedOperator =
      typeof newOperator === 'string' ? newOperator.trim() : newOperator;
    const oldOperator = condition[operatorKey] || '';
    const wasMultiple = isMultipleValuesAllowed(selectedField, oldOperator);
    const isMultiple = isMultipleValuesAllowed(selectedField, newOperator);
    const wasBetween = isBetweenOperator(oldOperator);
    const nextBetween = isBetweenOperator(trimmedOperator);

    // Clear value when switching from multiple to single selection
    if (wasMultiple && !isMultiple) {
      onChange(index, valueKey, '');
      onChange(index, 'displayValue', '');
    }

    if (wasBetween !== nextBetween) {
      onChange(index, valueKey, '');
      onChange(index, 'displayValue', '');
    }

    onChange(index, operatorKey, trimmedOperator);
  };

  // Handle value change
  const handleLoggedUserFieldSelect = (fieldName) => {
    const encoded = fieldName ? encodeLoggedUserCompareValue(fieldName) : '';
    valueDraftRef.current.loggedUserValue = encoded;
    patchCondition({
      [valueTypeKey]: FILTER_VALUE_TYPE_LOGGED_USER,
      [valueKey]: encoded,
      displayValue: '',
    });
  };

  const handleValueChange = (newValue) => {
    if (valueType === FILTER_VALUE_TYPE_FIELD) {
      handleFieldCompareSelect(newValue);
      return;
    }
    if (valueType === FILTER_VALUE_TYPE_LOGGED_USER) {
      handleLoggedUserFieldSelect(newValue);
      return;
    }
    if (valueType === FILTER_VALUE_TYPE_LITERAL) {
      valueDraftRef.current.literalValue = newValue;
      setDateLiteralInvalid(false);
      onChange(index, valueKey, newValue);
      return;
    }
    valueDraftRef.current.staticValue = newValue;
    onChange(index, valueKey, newValue);
  };

  const handleFieldCompareSelect = (picked) => {
    let encoded = '';
    if (compareStoreAsFieldConfigId) {
      const id = String(picked ?? '').trim();
      encoded = id ? encodeFieldCompareValue(id) : '';
    } else {
      const apiName = resolveFieldApiNameForCompare(
        comparableValueFields,
        picked,
        nameKey,
        labelKey
      );
      encoded = apiName ? encodeFieldCompareValue(apiName) : '';
    }
    valueDraftRef.current.fieldValue = encoded;
    onChange(index, valueTypeKey, FILTER_VALUE_TYPE_FIELD);
    onChange(index, valueKey, encoded);
  };

  const fieldCompareDropdownValue = compareStoreAsFieldConfigId
    ? resolveReportFieldComparePickerValue(
        comparableValueFields,
        condition[valueKey] || '',
        nameKey
      )
    : resolveFieldApiNameForCompare(
        comparableValueFields,
        condition[valueKey] || '',
        nameKey,
        labelKey
      );

  const loggedUserDropdownValue = decodeLoggedUserCompareValue(
    condition[valueKey] || ''
  );

  const legacyValueFixRef = useRef(false);
  useEffect(() => {
    legacyValueFixRef.current = false;
    valueDraftRef.current = {
      staticValue: '',
      fieldValue: '',
      loggedUserValue: '',
      literalValue: '',
      staticDisplayValue: '',
    };
    setDateLiteralInvalid(false);
    syncDraftFromCondition();
  }, [fieldName, index]);

  useEffect(() => {
    if (legacyValueFixRef.current) return;
    if (valueType !== FILTER_VALUE_TYPE_FIELD) return;
    if (!comparableValueFields?.length) return;
    const raw = condition[valueKey];
    if (raw == null || String(raw).trim() === '') return;
    if (isLoggedUserMergeToken(raw)) {
      legacyValueFixRef.current = true;
      const api = decodeLoggedUserCompareValue(raw);
      const encoded = encodeLoggedUserCompareValue(api);
      valueDraftRef.current.loggedUserValue = encoded;
      patchCondition({
        [valueTypeKey]: FILTER_VALUE_TYPE_LOGGED_USER,
        [valueKey]: encoded,
      });
      return;
    }
    if (isFieldMergeToken(raw)) {
      const api = decodeFieldCompareValue(raw);
      let resolved;
      let encoded;
      if (compareStoreAsFieldConfigId) {
        const pickerVal = resolveReportFieldComparePickerValue(
          comparableValueFields,
          raw,
          nameKey
        );
        resolved = pickerVal;
        encoded = pickerVal ? encodeFieldCompareValue(pickerVal) : '';
      } else {
        resolved = resolveFieldApiNameForCompare(
          comparableValueFields,
          api,
          nameKey,
          labelKey
        );
        encoded = resolved ? encodeFieldCompareValue(resolved) : '';
      }
      if (resolved && encoded !== String(raw).trim()) {
        legacyValueFixRef.current = true;
        valueDraftRef.current.fieldValue = encoded;
        onChange(index, valueKey, encoded);
      }
      return;
    }
    if (compareStoreAsFieldConfigId) {
      const pickerVal = resolveReportFieldComparePickerValue(
        comparableValueFields,
        raw,
        nameKey
      );
      if (pickerVal) {
        legacyValueFixRef.current = true;
        const encoded = encodeFieldCompareValue(pickerVal);
        valueDraftRef.current.fieldValue = encoded;
        patchCondition({
          [valueTypeKey]: FILTER_VALUE_TYPE_FIELD,
          [valueKey]: encoded,
        });
      }
      return;
    }
    const api = resolveFieldApiNameForCompare(
      comparableValueFields,
      raw,
      nameKey,
      labelKey
    );
    if (api) {
      legacyValueFixRef.current = true;
      const encoded = encodeFieldCompareValue(api);
      valueDraftRef.current.fieldValue = encoded;
      patchCondition({
        [valueTypeKey]: FILTER_VALUE_TYPE_FIELD,
        [valueKey]: encoded,
      });
    }
  }, [
    valueType,
    condition[valueKey],
    comparableValueFields,
    nameKey,
    labelKey,
    index,
    valueKey,
    valueTypeKey,
    onChange,
  ]);

  const handleValueBlur = () => {
    if (
      valueType === FILTER_VALUE_TYPE_FIELD ||
      valueType === FILTER_VALUE_TYPE_LOGGED_USER
    ) {
      return;
    }
    const current = condition[valueKey];
    if (typeof current !== 'string') return;

    if (valueType === FILTER_VALUE_TYPE_LITERAL && isDateField) {
      const check = isBetween
        ? validateDateFilterBetweenLiteralValue(current)
        : validateDateFilterLiteralValue(current);
      setDateLiteralInvalid(!check.valid);
      const normalized = normalizeFilterValue(current);
      if (normalized !== current) {
        onChange(index, valueKey, normalized);
      }
      return;
    }

    const normalized = normalizeFilterValue(current);
    if (normalized !== current) {
      onChange(index, valueKey, normalized);
    }
  };

  const effectiveOperatorCol = hideFieldColumn ? operatorColSize || 4 : operatorColSize;
  const effectiveValueCol = hideFieldColumn ? valueColSize || 8 : valueColSize;

  const betweenParts = parseBetweenFilterValue(condition[valueKey]);
  const useDatePicker = isDateField && valueType === FILTER_VALUE_TYPE_STATIC;
  const useLiteralBetweenInputs =
    isBetween && isDateField && valueType === FILTER_VALUE_TYPE_LITERAL;
  const useLiteralTextInput =
    isDateField && valueType === FILTER_VALUE_TYPE_LITERAL && !isBetween;

  const valueInputType = (() => {
    const dt = fieldDataType;
    if (useDatePicker) return 'date';
    // Match EditLayout: time datatype uses native time picker (HH:MM)
    if (dt === 'time' && valueType === FILTER_VALUE_TYPE_STATIC) return 'time';
    if (isNumericDataType(dt)) return 'number';
    return 'text';
  })();

  /** Normalize stored time values to HH:MM for <input type="time"> (same as EditLayout). */
  const toTimeInputValue = (raw) => {
    if (raw == null || raw === '') return '';
    let timeValue = String(raw);
    if (timeValue.includes('T') || timeValue.includes(' ')) {
      const d = new Date(timeValue);
      if (!Number.isNaN(d.getTime())) {
        return (
          String(d.getHours()).padStart(2, '0') +
          ':' +
          String(d.getMinutes()).padStart(2, '0')
        );
      }
    }
    if (timeValue.includes(':')) {
      const parts = timeValue.split(':');
      return parts[0] + ':' + parts[1];
    }
    return timeValue;
  };

  const formatValueForInput = (raw) => {
    if (valueInputType === 'time') return toTimeInputValue(raw);
    return raw ?? '';
  };

  const valuePlaceholder = (() => {
    if (!valueRequired) return 'N/A';
    if (valueType === FILTER_VALUE_TYPE_LITERAL && isDateField) {
      if (isBetween) return 'TODAY|LAST_N_DAYS:7';
      return 'TODAY or YYYY-MM-DD';
    }
    if (isAutonumberDataType(fieldDataType)) {
      return 'Numeric part only (prefix applied on save)';
    }
    if (showLoggedUserValueOption) {
      return 'Type {{LoggedUser. for suggestions';
    }
    return 'Enter value';
  })();

  const valueTypeSelectWidth = showLiteralValueOption
    ? '9.75rem'
    : showLoggedUserValueOption
      ? '8.5rem'
      : '7.5rem';

  const handleBetweenFromChange = (from) => {
    onChange(
      index,
      valueKey,
      encodeBetweenFilterValue(from, betweenParts.to)
    );
  };

  const handleBetweenToChange = (to) => {
    onChange(
      index,
      valueKey,
      encodeBetweenFilterValue(betweenParts.from, to)
    );
  };

  return (
    <Row className="align-items-end condition-row-flex">
      {/* Field Name Selector */}
      {!hideFieldColumn && (
      <Col md={fieldColSize}>
        <Form.Group className="mb-3">
          <Form.Label style={standardLabelStyles}>Field Name</Form.Label>
          {useSearchableDropdown ? (
            <div style={standardInputContainerHeight}>
              <FieldSelectorDropdown
                fields={fields}
                value={fieldName}
                onChange={handleFieldChange}
                nameKey={nameKey}
                labelKey={labelKey}
                placeholder="-Select Field-"
                disabled={readOnly || fieldReadOnly}
                size=""
                fieldTypeFilter={fieldTypeFilter}
                fieldFilter={fieldFilter}
                hideSubtitle={hideSubtitle}
                subtitleKey={subtitleKey}
              />
            </div>
          ) : (
            <Form.Select
              value={fieldName}
              onChange={(e) => handleFieldChange(e.target.value)}
              disabled={readOnly || fieldReadOnly}
              style={standardInputStyles}
            >
              <option value="">-Select Field-</option>
              {fields.map((field) => {
                const fieldValue = field[nameKey] || field.name || '';
                const fieldLabel = field[labelKey] || field.label || field.field_name || fieldValue;
                return (
                  <option key={fieldValue} value={fieldValue}>
                    {fieldLabel}
                  </option>
                );
              })}
            </Form.Select>
          )}
        </Form.Group>
      </Col>
      )}

      {/* Operator Selector */}
      <Col md={effectiveOperatorCol}>
        <Form.Group className="mb-3">
          <Form.Label style={standardLabelStyles}>Operator</Form.Label>
          <Form.Select
            value={condition[operatorKey] || ''}
            onChange={(e) => handleOperatorChange(e.target.value)}
            disabled={readOnly || !fieldName}
            style={standardInputStyles}
          >
            <option value="">Select</option>
            {operators.map((op) => {
              const opValue = typeof op === 'string' ? op : op.value;
              const opLabel = typeof op === 'string' ? op : op.label;
              return (
                <option key={opValue} value={opValue}>
                  {opLabel}
                </option>
              );
            })}
          </Form.Select>
        </Form.Group>
      </Col>

      {/* Value Input */}
      <Col md={effectiveValueCol}>
        <Form.Group className="mb-3">
          <Form.Label style={standardLabelStyles}>
            Value
            {isPicklistField(selectedField) && allowMultiple && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id={`multipicklist-tooltip-${index}`}>
                    Select multiple values. Values will be joined with semicolons.
                  </Tooltip>
                }
              >
                <span
                  className="ms-1 d-inline-flex align-items-center"
                  role="img"
                  aria-label="Multipicklist help"
                >
                  <FaInfoCircle
                    className="text-muted"
                    style={{ cursor: 'help', fontSize: '0.875rem' }}
                  />
                </span>
              </OverlayTrigger>
            )}
          </Form.Label>
          <div
            className="d-flex gap-2 align-items-stretch"
            style={{ minWidth: 0 }}
          >
            {showValueTypeToggle && (
              <Form.Select
                value={valueType}
                onChange={(e) => handleValueTypeChange(e.target.value)}
                disabled={readOnly || !fieldName}
                style={{
                  ...standardInputStyles,
                  width: valueTypeSelectWidth,
                  minWidth: valueTypeSelectWidth,
                  flexShrink: 0,
                }}
                aria-label="Value source"
              >
                <option value={FILTER_VALUE_TYPE_STATIC}>Static</option>
                <option value={FILTER_VALUE_TYPE_FIELD}>Field</option>
                {showLoggedUserValueOption && (
                  <option value={FILTER_VALUE_TYPE_LOGGED_USER}>Logged User</option>
                )}
                {showLiteralValueOption && (
                  <option value={FILTER_VALUE_TYPE_LITERAL}>Date Literal</option>
                )}
              </Form.Select>
            )}
            <div
              className="flex-grow-1"
              style={{
                minWidth: 0,
                ...(useSearchableDropdown ||
                isPicklistField(selectedField) ||
                isLookupField
                  ? standardInputContainerHeight
                  : {}),
              }}
            >
              {isBetween &&
              valueRequired &&
              valueType === FILTER_VALUE_TYPE_FIELD ? (
                <FieldSelectorDropdown
                  fields={comparableValueFields}
                  value={fieldCompareDropdownValue}
                  onChange={handleFieldCompareSelect}
                  nameKey={nameKey}
                  labelKey={labelKey}
                  placeholder="-Select field to compare-"
                  disabled={readOnly || !fieldName}
                  hideSubtitle={hideSubtitle}
                  subtitleKey={subtitleKey}
                  closedDisplay="mergeToken"
                />
              ) : isBetween && valueRequired ? (
                useLiteralBetweenInputs ? (
                  <div className="d-flex gap-2 align-items-center w-100">
                    <Form.Control
                      type="text"
                      value={betweenParts.from}
                      onChange={(e) => handleBetweenFromChange(e.target.value)}
                      onBlur={handleValueBlur}
                      disabled={readOnly || !fieldName}
                      placeholder="From (TODAY)"
                      isInvalid={dateLiteralInvalid}
                      style={standardInputStyles}
                      aria-label="Range from"
                    />
                    <span className="text-muted small flex-shrink-0">and</span>
                    <Form.Control
                      type="text"
                      value={betweenParts.to}
                      onChange={(e) => handleBetweenToChange(e.target.value)}
                      onBlur={handleValueBlur}
                      disabled={readOnly || !fieldName}
                      placeholder="To (YYYY-MM-DD)"
                      isInvalid={dateLiteralInvalid}
                      style={standardInputStyles}
                      aria-label="Range to"
                    />
                  </div>
                ) : (
                <div className="d-flex gap-2 align-items-center w-100">
                  {showLoggedUserValueOption ? (
                    <>
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <LoggedUserMergeTextInput
                          type={valueInputType}
                          value={formatValueForInput(betweenParts.from)}
                          onChange={handleBetweenFromChange}
                          disabled={readOnly || !fieldName}
                          placeholder="From"
                          style={standardInputStyles}
                          aria-label="Range from"
                        />
                      </div>
                      <span className="text-muted small flex-shrink-0">and</span>
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <LoggedUserMergeTextInput
                          type={valueInputType}
                          value={formatValueForInput(betweenParts.to)}
                          onChange={handleBetweenToChange}
                          disabled={readOnly || !fieldName}
                          placeholder="To"
                          style={standardInputStyles}
                          aria-label="Range to"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <Form.Control
                        type={valueInputType}
                        value={formatValueForInput(betweenParts.from)}
                        onChange={(e) => handleBetweenFromChange(e.target.value)}
                        disabled={readOnly || !fieldName}
                        placeholder="From"
                        style={standardInputStyles}
                        aria-label="Range from"
                      />
                      <span className="text-muted small flex-shrink-0">and</span>
                      <Form.Control
                        type={valueInputType}
                        value={formatValueForInput(betweenParts.to)}
                        onChange={(e) => handleBetweenToChange(e.target.value)}
                        disabled={readOnly || !fieldName}
                        placeholder="To"
                        style={standardInputStyles}
                        aria-label="Range to"
                      />
                    </>
                  )}
                </div>
                )
              ) : useLiteralTextInput && valueRequired ? (
                <Form.Control
                  type="text"
                  value={condition[valueKey] || ''}
                  onChange={(e) => handleValueChange(e.target.value)}
                  onBlur={handleValueBlur}
                  disabled={readOnly || !valueRequired}
                  placeholder={valuePlaceholder}
                  isInvalid={dateLiteralInvalid}
                  style={standardInputStyles}
                  aria-label="Date Literal value"
                />
              ) : valueType === FILTER_VALUE_TYPE_FIELD && valueRequired ? (
                <FieldSelectorDropdown
                  fields={comparableValueFields}
                  value={fieldCompareDropdownValue}
                  onChange={handleFieldCompareSelect}
                  nameKey={nameKey}
                  labelKey={labelKey}
                  placeholder="-Select field to compare-"
                  disabled={readOnly || !fieldName}
                  hideSubtitle={hideSubtitle}
                  subtitleKey={subtitleKey}
                  closedDisplay="mergeToken"
                />
              ) : valueType === FILTER_VALUE_TYPE_LOGGED_USER && valueRequired ? (
                <Form.Select
                  value={loggedUserDropdownValue}
                  onChange={(e) => handleLoggedUserFieldSelect(e.target.value)}
                  disabled={readOnly || !fieldName}
                  style={standardInputStyles}
                  aria-label="Current user field"
                >
                  <option value="">-Select current user field-</option>
                  {LOGGED_USER_COMPARE_FIELDS.map((f) => (
                    <option key={f.field_name} value={f.field_name}>
                      {f.field_label} ({`{{LoggedUser.${f.field_name}}}`})
                    </option>
                  ))}
                </Form.Select>
              ) : isPicklistField(selectedField) ? (
                <PicklistFieldSelector
                  field={selectedField}
                  value={condition[valueKey] || ''}
                  onChange={handleValueChange}
                  disabled={readOnly || !valueRequired}
                  isMultiSelect={allowMultiple}
                  prefetchedPicklistOptions={
                    picklistPrefetchByFieldId != null && selectedField != null
                      ? picklistPrefetchByFieldId[
                          String(
                            selectedField.id ??
                              selectedField.field_config_id ??
                              selectedField.field_id ??
                              ''
                          )
                        ]
                      : undefined
                  }
                />
              ) : isBooleanField(selectedField) ? (
                <Form.Select
                  value={condition[valueKey] || ''}
                  onChange={(e) => handleValueChange(e.target.value)}
                  disabled={readOnly || !valueRequired}
                  style={standardInputStyles}
                >
                  <option value="">-Select-</option>
                  <option value="true">True</option>
                  <option value="false">False</option>
                </Form.Select>
              ) : isLookupField ? (
                (() => {
                  const { lookupFilterArray, lookupFilterLogic } =
                    extractLookupFilter(selectedField);
                  const displayLabel = condition.displayValue || '';
                  return (
                    <LookupLayout
                      objectName={
                        selectedField.reference_object ||
                        selectedField.referenceObject
                      }
                      searchFields={
                        selectedField.lookup_search_fields ||
                        selectedField.lookupSearchFields ||
                        'name'
                      }
                      displayFields={
                        selectedField.display_fields ||
                        selectedField.displayFields ||
                        'name'
                      }
                      value={condition[valueKey] || ''}
                      defaultLabel={displayLabel}
                      onChange={(id, label) => {
                        const nextId =
                          id != null && String(id).trim() !== '' ? id : '';
                        handleValueChange(nextId);
                      const nextLabel =
                        label != null && String(label).trim() !== ''
                          ? label
                          : '';
                      valueDraftRef.current.staticDisplayValue = nextLabel;
                      onChange(index, 'displayValue', nextLabel);
                      }}
                      disabled={readOnly || !valueRequired}
                      placeholder={valueRequired ? 'Search...' : 'N/A'}
                      isRequired={valueRequired}
                      lookupFilter={lookupFilterArray}
                      lookupFilterLogic={lookupFilterLogic}
                      scopeRecordviewToObjectName={scopeRecordviewToObjectNameForField(
                        selectedField,
                        layoutObjectName
                      )}
                    />
                  );
                })()
              ) : showLoggedUserValueOption ? (
                <LoggedUserMergeTextInput
                  type={valueInputType}
                  value={formatValueForInput(condition[valueKey] || '')}
                  onChange={handleValueChange}
                  onBlur={handleValueBlur}
                  disabled={readOnly || !valueRequired}
                  placeholder={valuePlaceholder}
                  style={standardInputStyles}
                />
              ) : (
                <Form.Control
                  type={valueInputType}
                  value={formatValueForInput(condition[valueKey] || '')}
                  onChange={(e) => handleValueChange(e.target.value)}
                  onBlur={handleValueBlur}
                  disabled={readOnly || !valueRequired}
                  placeholder={valuePlaceholder}
                  style={standardInputStyles}
                />
              )}
            </div>
          </div>
        </Form.Group>
      </Col>
    </Row>
  );
}
